import React, { useEffect, useMemo, useState } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { api } from "../api";
import { Bell } from "lucide-react";
import BirthdayConfettiOverlay from "./BirthdayConfettiOverlay";
import { useNavigate } from "react-router-dom";
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  ChartDataLabels,
);

const PALETTE = [
  "#343A76",
  "#4E79A7",
  "#F28E2B",
  "#E15759",
  "#76B7B2",
  "#59A14F",
  "#EDC948",
  "#B07AA1",
  "#FF9DA7",
  "#9C755F",
];

const getColor = (i) => PALETTE[i % PALETTE.length];

const monthKey = (d) => {
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
};

const shortLabel = (text, max = 26) =>
  text?.length > max ? text.slice(0, max - 1) + "…" : text || "";

const EmployeeDashboard = ({ user }) => {
  const normalizedRole = String(user?.role || "")
    .trim()
    .toLowerCase();

  // Birthday preparation reminder sirf HR team ko dikhana hai.
  // "HR Head" aapke current User role list me hai; "HR Manager" ko bhi
  // future compatibility ke liye support kiya gaya hai.
  const isHrBirthdayViewer = ["hr executive", "hr head", "hr manager"].includes(
    normalizedRole,
  );

  const [monthlySummary, setMonthlySummary] = useState({});
  const [selectedMonth, setSelectedMonth] = useState("");
  const [monthlyReviewSummary, setMonthlyReviewSummary] = useState({});
  const [showAllProjects, setShowAllProjects] = useState(false);
  const [logsAll, setLogsAll] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [birthdayMessage, setBirthdayMessage] = useState("");
  const [showBirthdayMessage, setShowBirthdayMessage] = useState(false);
  const navigate = useNavigate();
  const [vw, setVw] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200,
  );

  const isMobile = vw <= 768;
  useEffect(() => {
    if (!user?._id) return;

    const fetchBirthdayMessage = async () => {
      try {
        const res = await api.get("/birthday-message/me", {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });

        setShowBirthdayMessage(!!res.data?.show);
        setBirthdayMessage(res.data?.message || "");
      } catch (err) {
        console.error("Birthday message fetch failed:", err);
        setShowBirthdayMessage(false);
        setBirthdayMessage("");
      }
    };

    fetchBirthdayMessage();
  }, [user]);

  useEffect(() => {
    const onResize = () => setVw(window.innerWidth);
    window.addEventListener("resize", onResize, { passive: true });
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!user?._id) return;

    const loadNotifications = async () => {
      const res = await api.get("/notifications/my", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setNotifications(res.data || []);
    };

    loadNotifications();
  }, [user]);

  useEffect(() => {
    if (!user) return;

    const fetchData = async () => {
      try {
        /* ================= REVIEW API ================= */
        const reviewRes = await api.get(`/employee-review/my/${user._id}`);
        const reviews = Array.isArray(reviewRes.data) ? reviewRes.data : [];

        const reviewSummary = {};

        reviews.forEach((r) => {
          const key = monthKey(r.createdAt || r.assignedAt);

          if (!reviewSummary[key]) {
            reviewSummary[key] = {
              positive: 0,
              improvement: 0,
              achieved: 0,
              pending: 0,
            };
          }

          reviewSummary[key].positive += Object.keys(
            r.positivePoints || {},
          ).length;

          reviewSummary[key].improvement += Object.keys(
            r.improvementAreas || {},
          ).length;

          r.status === "ACHIEVED"
            ? reviewSummary[key].achieved++
            : reviewSummary[key].pending++;
        });

        setMonthlyReviewSummary(reviewSummary);

        /* ================= LOGS + LEAVES ================= */
        const [logRes, leaveRes] = await Promise.all([
          api.get("/logs/my", {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }),
          api.get("/leaves/my", {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }),
        ]);

        const logs = Array.isArray(logRes.data) ? logRes.data : [];
        setLogsAll(logs);

        const summary = {};

        logs.forEach((log) => {
          const project = log.projectName || "Others";
          const hours = parseFloat(log.actualHours) || 0;
          const key = monthKey(log.date);

          if (!summary[key]) {
            summary[key] = {
              totalHours: 0,
              leaves: 0,
              projectCount: {},
            };
          }

          summary[key].totalHours += hours;
          summary[key].projectCount[project] =
            (summary[key].projectCount[project] || 0) + hours;
        });

        (Array.isArray(leaveRes.data) ? leaveRes.data : [])
          .filter((l) => l.status === "Approved")
          .forEach((leave) => {
            const start = new Date(leave.fromDate);
            const end = new Date(leave.toDate);

            for (
              let d = new Date(start);
              d <= end;
              d.setDate(d.getDate() + 1)
            ) {
              const key = monthKey(d);
              if (!summary[key]) {
                summary[key] = {
                  totalHours: 0,
                  leaves: 0,
                  projectCount: {},
                };
              }
              summary[key].leaves += 1;
            }
          });

        setMonthlySummary(summary);

        const monthKeys = Object.keys(summary).sort().reverse();
        if (monthKeys.length > 0) setSelectedMonth(monthKeys[0]);
      } catch (err) {
        console.error("Error loading data:", err);
      }
    };

    fetchData();
  }, [user]);

  const getMonthLabel = (key) => {
    const date = new Date(`${key}-01`);
    return date.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  };

  const selectedData = monthlySummary[selectedMonth] || {
    totalHours: 0,
    leaves: 0,
    projectCount: {},
  };

  // ---------- Charts: Project-wise ----------
  const sortedProjects = useMemo(
    () => Object.entries(selectedData.projectCount).sort((a, b) => b[1] - a[1]),
    [selectedData],
  );
  const visibleProjects = showAllProjects
    ? sortedProjects
    : sortedProjects.slice(0, 10);

  const projectFullLabels = visibleProjects.map(([name]) => name);
  const projectLabels = projectFullLabels.map((name) =>
    shortLabel(name, isMobile ? 18 : 28),
  );
  const projectHours = visibleProjects.map(([, hours]) => hours);

  const barThickness = isMobile ? 22 : 36;
  const rowGap = isMobile ? 10 : 14;
  const chartH = Math.max(
    180,
    visibleProjects.length * (barThickness + rowGap),
  );

  const projectBarOptions = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        beginAtZero: true,
        title: { display: true, text: "Hours" },
        ticks: { font: { size: isMobile ? 10 : 12 } },
      },
      y: {
        ticks: { autoSkip: false, font: { size: isMobile ? 10 : 12 } },
        title: { display: true, text: "Projects" },
      },
    },
    elements: {
      bar: {
        barThickness,
        maxBarThickness: barThickness,
        categoryPercentage: 0.9,
        barPercentage: 0.9,
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (items) => {
            const idx = items?.[0]?.dataIndex ?? 0;
            return projectFullLabels[idx] || "";
          },
          label: (item) => `${item.raw?.toFixed?.(2) ?? item.raw} hrs`,
        },
      },
      datalabels: {
        anchor: "end",
        align: "right",
        color: "#000",
        backgroundColor: "rgba(255,255,255,0.7)",
        borderRadius: 4,
        padding: { top: 2, bottom: 2, left: 4, right: 4 },
        font: { weight: "bold", size: isMobile ? 9 : 11 },
        formatter: (v) => (v >= 0.25 ? `${Number(v).toFixed(1)}` : ""), // hide tiny labels
        clip: true,
      },
    },
  };

  const projectBarData = {
    labels: projectLabels,
    datasets: [
      {
        label: "Hours Spent",
        data: projectHours,
        backgroundColor: projectLabels.map((_, i) => getColor(i)),
        borderRadius: 10,
        customLabelsFull: projectFullLabels, // for tooltips
      },
    ],
  };

  // ---------- Charts: Work-type (selected month only) ----------
  const monthLogs = useMemo(
    () => logsAll.filter((l) => monthKey(l.date) === selectedMonth),
    [logsAll, selectedMonth],
  );

  const workTypeSummary = useMemo(() => {
    const acc = {};
    monthLogs.forEach((log) => {
      const drawing = log.drawingDocument || "Unknown";
      const hours = parseFloat(log.actualHours || 0);
      if (!acc[drawing]) acc[drawing] = { totalHours: 0, entries: [] };
      acc[drawing].totalHours += hours;
      acc[drawing].entries.push({
        projectName: log.projectName || "Untitled",
        drawing, // <— drawing/document name
        workType: log.workType || "-", // <— actual work type
        hours,
      });
    });
    return acc;
  }, [monthLogs]);

  const workTypeLabelsFull = Object.keys(workTypeSummary).sort(
    (a, b) => workTypeSummary[b].totalHours - workTypeSummary[a].totalHours,
  );
  const workTypeLabels = workTypeLabelsFull.map((t) =>
    shortLabel(t, isMobile ? 18 : 28),
  );
  const workTypeHours = workTypeLabelsFull.map(
    (type) => workTypeSummary[type].totalHours,
  );

  const workTypeH = Math.max(
    180,
    workTypeLabels.length * (barThickness + rowGap),
  );

  const workTypeOptions = {
    ...projectBarOptions,
    plugins: {
      ...projectBarOptions.plugins,
      tooltip: {
        callbacks: {
          title: (items) => {
            const idx = items?.[0]?.dataIndex ?? 0;
            return workTypeLabelsFull[idx] || "";
          },
          label: (item) => `${item.raw?.toFixed?.(2) ?? item.raw} hrs`,
        },
      },
    },
  };

  const workTypeData = {
    labels: workTypeLabels,
    datasets: [
      {
        label: "Hours Spent",
        data: workTypeHours,
        backgroundColor: workTypeLabels.map((_, i) => getColor(i)),
        borderRadius: 10,
        customLabelsFull: workTypeLabelsFull,
      },
    ],
  };
  // 👉 REVIEW type ke unread notifications
  const reviewNotifications = notifications.filter(
    (n) => n.type === "REVIEW" && !n.isRead,
  );

  // 🎂 Birthday preparation reminders sirf HR Executive / HR Head /
  // HR Manager ko Employee Dashboard par dikhengi.
  const birthdayNotifications = isHrBirthdayViewer
    ? notifications.filter((n) => n.type === "BIRTHDAY_REMINDER" && !n.isRead)
    : [];

  // Bell count me HR ke birthday reminders bhi include honge.
  const unreadCount = reviewNotifications.length + birthdayNotifications.length;
  // ---------- UI ----------
  const cardStyle = {
    background: "#fff",
    padding: isMobile ? "14px" : "20px",
    borderRadius: "12px",
    boxShadow: "0 3px 6px rgba(0,0,0,0.1)",
    minWidth: 0,
    textAlign: "center",
    fontSize: isMobile ? 14 : 16,
  };

  return (
    <div style={{ padding: isMobile ? "12px" : "20px" }}>
      {showBirthdayMessage && <BirthdayConfettiOverlay userName={user?.name} />}
      <h2 style={{ color: "#343A76", textAlign: "center", margin: "0 0 8px" }}>
        Employee Dashboard
      </h2>
      {showBirthdayMessage && (
        <div
          style={{
            background:
              "linear-gradient(135deg, #fff7ed 0%, #fff1f2 50%, #fefce8 100%)",
            border: "1px solid #f9a8d4",
            borderRadius: 20,
            marginBottom: 24,
            boxShadow: "0 10px 24px rgba(0,0,0,0.08)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: isMobile ? "14px 16px 10px" : "18px 24px 12px",
              borderBottom: "1px solid rgba(236, 72, 153, 0.15)",
              textAlign: "center",
              background: "rgba(255,255,255,0.35)",
            }}
          >
            <div
              style={{
                fontSize: isMobile ? 24 : 30,
                marginBottom: 4,
              }}
            >
              🎉🎂✨
            </div>

            <div
              style={{
                fontSize: isMobile ? 24 : 30,
                fontWeight: 800,
                color: "#9d174d",
                lineHeight: 1.2,
                letterSpacing: 0.3,
              }}
            >
              Happy Birthday, {user?.name}!
            </div>

            <div
              style={{
                marginTop: 6,
                fontSize: isMobile ? 13 : 14,
                color: "#7c2d12",
                fontWeight: 600,
              }}
            >
              Best wishes from your team
            </div>
          </div>

          <div
            style={{
              padding: isMobile ? "16px" : "22px 28px",
              textAlign: "center",
            }}
          >
            <div
              style={{
                maxWidth: 980,
                margin: "0 auto",
                fontSize: isMobile ? 15 : 18,
                lineHeight: isMobile ? 1.8 : 1.95,
                color: "#4b5563",
                fontWeight: 500,
              }}
            >
              {birthdayMessage}
            </div>

            <div
              style={{
                marginTop: 18,
                display: "inline-block",
                padding: isMobile ? "8px 14px" : "10px 18px",
                borderRadius: 999,
                background: "rgba(255,255,255,0.75)",
                border: "1px solid rgba(236, 72, 153, 0.2)",
                fontSize: isMobile ? 13 : 14,
                fontWeight: 700,
                color: "#9d174d",
              }}
            >
              With warm wishes from Sheth Design Consultants LLP
            </div>
          </div>
        </div>
      )}
      {/* 🎂 HR-ONLY UPCOMING BIRTHDAY REMINDERS */}
      {isHrBirthdayViewer && birthdayNotifications.length > 0 && (
        <div
          style={{
            background:
              "linear-gradient(135deg, #fff7ed 0%, #fff1f2 55%, #fefce8 100%)",
            border: "1px solid #f9a8d4",
            padding: isMobile ? "14px" : "16px 18px",
            borderRadius: 14,
            marginBottom: 20,
            boxShadow: "0 6px 18px rgba(157, 23, 77, 0.08)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: isMobile ? "flex-start" : "center",
              gap: 14,
              flexDirection: isMobile ? "column" : "row",
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 7,
                }}
              >
                <span style={{ fontSize: 24 }}>🎂</span>
                <b style={{ color: "#9d174d", fontSize: 16 }}>
                  Upcoming Employee Birthday
                </b>
              </div>

              <div
                style={{
                  fontSize: 12,
                  color: "#7c2d12",
                  fontWeight: 700,
                  marginBottom: 10,
                }}
              >
                Birthday preparation reminder for HR
              </div>

              <div
                style={{
                  display: "grid",
                  gap: 8,
                }}
              >
                {birthdayNotifications.map((notification) => (
                  <div
                    key={notification._id}
                    style={{
                      background: "rgba(255,255,255,0.78)",
                      border: "1px solid rgba(236,72,153,0.16)",
                      borderRadius: 10,
                      padding: "10px 12px",
                    }}
                  >
                    <strong
                      style={{
                        display: "block",
                        color: "#831843",
                        marginBottom: 3,
                        fontSize: 13.5,
                      }}
                    >
                      {notification.title || "🎂 Upcoming Employee Birthday"}
                    </strong>

                    <span
                      style={{
                        color: "#4b5563",
                        fontSize: 13,
                        lineHeight: 1.55,
                      }}
                    >
                      {notification.message}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <button
              type="button"
              onClick={async () => {
                try {
                  await Promise.all(
                    birthdayNotifications.map((notification) =>
                      api.put(
                        `/notifications/${notification._id}/read`,
                        {},
                        {
                          headers: {
                            Authorization: `Bearer ${localStorage.getItem(
                              "token",
                            )}`,
                          },
                        },
                      ),
                    ),
                  );

                  setNotifications((prev) =>
                    prev.map((item) =>
                      birthdayNotifications.some(
                        (birthday) => birthday._id === item._id,
                      )
                        ? { ...item, isRead: true }
                        : item,
                    ),
                  );
                } catch (err) {
                  console.error("Birthday notification read failed:", err);
                }
              }}
              style={{
                flexShrink: 0,
                background: "#9d174d",
                color: "#fff",
                border: "none",
                padding: "9px 14px",
                borderRadius: 8,
                cursor: "pointer",
                fontWeight: 700,
                fontSize: 13,
              }}
            >
              Mark as read
            </button>
          </div>
        </div>
      )}

      {/* 🔔 REVIEW ALERT STRIP (Leave style) */}
      {reviewNotifications.length > 0 && (
        <div
          style={{
            background: "#fff7ed",
            border: "1px solid #fed7aa",
            padding: "14px 18px",
            borderRadius: 12,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 20,
          }}
        >
          <div>
            <b>{reviewNotifications.length} review awaiting your action</b>
            <div style={{ fontSize: 13, color: "#6b7280" }}>
              Admin has submitted your performance review.
            </div>
          </div>

          <button
            onClick={async () => {
              // 👉 sab review notifications read mark
              await Promise.all(
                reviewNotifications.map((n) =>
                  api.put(`/notifications/${n._id}/read`),
                ),
              );

              // 👉 sidha review page
              navigate("/employee-reviews");
            }}
            style={{
              background: "#1e3a8a",
              color: "#fff",
              border: "none",
              padding: "8px 14px",
              borderRadius: 8,
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            Review now
          </button>
        </div>
      )}

      {/* 🔔 Notification Bell */}
      <div
        onClick={async () => {
          // Review notification ho to existing review flow same rahega.
          if (reviewNotifications.length > 0) {
            await Promise.all(
              reviewNotifications.map((n) =>
                api.put(
                  `/notifications/${n._id}/read`,
                  {},
                  {
                    headers: {
                      Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                  },
                ),
              ),
            );

            navigate("/employee/reviews");
            return;
          }

          // Sirf birthday reminder ho to bell click par wahi Employee Dashboard
          // par visible card rahega; automatic navigation nahi karenge.
          if (isHrBirthdayViewer && birthdayNotifications.length > 0) {
            return;
          }
        }}
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 16,
        }}
      >
        <Bell size={30} color="#facc15" />

        {unreadCount > 0 && (
          <span
            style={{
              position: "absolute",
              top: -6,
              right: -6,
              background: "#dc2626",
              color: "#fff",
              borderRadius: "50%",
              fontSize: 11,
              padding: "2px 6px",
              fontWeight: "bold",
            }}
          >
            {unreadCount}
          </span>
        )}
      </div>
      <p style={{ fontSize: isMobile ? "14px" : "16px", lineHeight: 1.5 }}>
        <span
          style={{
            fontSize: isMobile ? "18px" : "22px",
            color: "#343A76",
            fontWeight: "bold",
          }}
        >
          Hello {user?.name},
        </span>
        <br />
        You have been actively with us. Thank you for your dedication and focus
        on the work. The charts below help you analyse your monthly working
        pattern. Your current post is <b>{user?.role}</b>.
      </p>

      <div
        style={{
          margin: "16px 0 20px",
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
        }}
      >
        <label style={{ fontWeight: "bold" }}>Select Month:</label>
        <select
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
          style={{
            width: "auto",
            minWidth: 200,
            padding: "6px 12px",
            borderRadius: 8,
            border: "1px solid #cfd3d8",
          }}
        >
          {Object.keys(monthlySummary)
            .sort()
            .reverse()
            .map((key) => (
              <option key={key} value={key}>
                {getMonthLabel(key)}
              </option>
            ))}
        </select>
      </div>
      {monthlyReviewSummary[selectedMonth] && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: 16,
            marginBottom: 24,
          }}
        >
          {/* POSITIVE */}
          <div style={reviewCardStyle("#dcfce7", "#166534")}>
            ⭐ Positive
            <br />
            <b style={{ fontSize: 20 }}>
              {monthlyReviewSummary[selectedMonth].positive}
            </b>
          </div>

          {/* IMPROVEMENT */}
          <div style={reviewCardStyle("#fee2e2", "#991b1b")}>
            ⚠️ Improvement
            <br />
            <b style={{ fontSize: 20 }}>
              {monthlyReviewSummary[selectedMonth].improvement}
            </b>
          </div>

          {/* ACHIEVED */}
          <div style={reviewCardStyle("#fef3c7", "#92400e")}>
            🏆 Achieved
            <br />
            <b style={{ fontSize: 20 }}>
              {monthlyReviewSummary[selectedMonth].achieved}
            </b>
          </div>

          {/* PENDING */}
          <div style={reviewCardStyle("#e0e7ff", "#3730a3")}>
            ⏳ Pending
            <br />
            <b style={{ fontSize: 20 }}>
              {monthlyReviewSummary[selectedMonth].pending}
            </b>
          </div>
        </div>
      )}

      {/* KPI Cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
          gap: isMobile ? 10 : 16,
          marginBottom: isMobile ? 16 : 20,
        }}
      >
        <div style={cardStyle}>
          Total Hours
          <br />
          <b>{selectedData.totalHours.toFixed(2)} hrs</b>
        </div>
        <div style={cardStyle}>
          Projects
          <br />
          <b>{Object.keys(selectedData.projectCount).length}</b>
        </div>
        <div style={cardStyle}>
          Leaves
          <br />
          <b>{selectedData.leaves}</b>
        </div>
      </div>

      {/* Project-wise chart */}
      <h4 style={{ color: "#343A76", margin: "8px 0" }}>
        Project-wise Hours Spent
      </h4>
      <div
        style={{
          width: "100%",
          overflowX: "auto",
          marginBottom: isMobile ? "24px" : "36px",
        }}
      >
        <div style={{ minWidth: 0, width: "100%", height: `${chartH}px` }}>
          <Bar
            data={projectBarData}
            options={projectBarOptions}
            plugins={[ChartDataLabels]}
          />
        </div>
      </div>

      {sortedProjects.length > 10 && (
        <div
          style={{
            textAlign: "center",
            marginBottom: isMobile ? "20px" : "28px",
          }}
        >
          <button
            onClick={() => setShowAllProjects(!showAllProjects)}
            style={{
              background: "#343A76",
              color: "#fff",
              padding: "8px 16px",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              fontSize: 14,
            }}
          >
            {showAllProjects ? "View Less" : "View More"}
          </button>
        </div>
      )}

      {/* Work-type chart */}
      <h4 style={{ color: "#343A76", margin: "8px 0" }}>
        Work Type-wise Analysis
      </h4>
      <div
        style={{
          width: "100%",
          maxWidth: "1600px",
          height: `${workTypeH}px`,
          marginTop: "12px",
        }}
      >
        <Bar
          data={workTypeData}
          options={workTypeOptions}
          plugins={[ChartDataLabels]}
        />
      </div>

      {/* Table */}
      <div
        style={{
          width: "100%",
          overflowX: "auto",
          marginTop: isMobile ? 16 : 24,
        }}
      >
        <table
          style={{
            width: "100%",
            minWidth: 640,
            borderCollapse: "collapse",
            fontSize: isMobile ? 12 : 14,
          }}
        >
          <thead>
            <tr style={{ backgroundColor: "#343A76", color: "#fff" }}>
              <th style={thStyle}>Project Name</th>
              <th style={thStyle}>Drawing</th>
              <th style={thStyle}>Work Type</th>
              <th style={thStyle}>Hours Spent</th>
            </tr>
          </thead>
          <tbody>
            {workTypeLabelsFull.flatMap((type) =>
              workTypeSummary[type].entries.map((entry, idx) => (
                <tr key={`${type}-${idx}`}>
                  <td style={tdStyle}>{entry.projectName}</td>
                  <td style={tdStyle}>{entry.drawing}</td>
                  <td style={tdStyle}>{entry.workType}</td>
                  <td style={tdStyle}>{entry.hours.toFixed(2)}</td>
                </tr>
              )),
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const thStyle = {
  padding: "10px",
  border: "1px solid #ccc",
  textAlign: "left",
  position: "sticky",
  top: 0,
};
const reviewCardStyle = (bg, color) => ({
  background: bg,
  color,
  padding: "18px",
  borderRadius: "14px",
  textAlign: "center",
  fontWeight: 700,
  boxShadow: "0 6px 14px rgba(0,0,0,0.08)",
});

const tdStyle = {
  padding: "10px",
  border: "1px solid #ccc",
  whiteSpace: "nowrap",
};

export default EmployeeDashboard;
